import * as React from "react"

import { cn } from "../lib/utils"

interface InputProps extends React.ComponentProps<"input"> {
  label?: string
  error?: string
  helperText?: string
  /**
   * Whether a `type="number"` box keeps its stepper arrows. Default `true`.
   *
   * The arrows are an invitation to nudge, which fits a quantity — points, a
   * weight, a position in an ordering — and misreads a number that is only
   * written down. Nobody arrives at a graduation year by pressing "up" 2027
   * times, and offering the control implies a scale that isn't there.
   *
   * `false` drops the chrome and nothing else: still `type="number"`, so a
   * phone still raises the numeric keypad and the box still refuses letters.
   */
  spinButtons?: boolean
}

/**
 * Chrome and Safari draw the arrows as pseudo-elements, which have to be
 * removed individually and un-reserve their gutter with them; Firefox reads
 * `appearance: textfield` on the input itself. All three are needed, so they
 * live together rather than at any call site.
 */
const NO_SPIN_BUTTONS =
  "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0 [&::-webkit-outer-spin-button]:m-0"

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, label, error, helperText, id, spinButtons = true, ...props }, ref) => {
    // Auto-generate ID from label if not provided
    const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined)

    const inputElement = (
      <input
        id={inputId}
        type={type}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          error && "border-destructive focus-visible:ring-destructive",
          "aria-[invalid=true]:border-destructive aria-[invalid=true]:focus-visible:ring-destructive",
          !spinButtons && NO_SPIN_BUTTONS,
          className
        )}
        ref={ref}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
        {...props}
      />
    )

    // When used inside FormControl (no label/error/helperText), return bare input
    // so Radix Slot can properly merge aria props onto the <input> element
    if (!label && !error && !helperText) {
      return inputElement
    }

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-neutral-700 mb-1"
          >
            {label}
          </label>
        )}
        {inputElement}
        {error && (
          <p
            id={`${inputId}-error`}
            className="mt-1 text-sm text-destructive"
          >
            {error}
          </p>
        )}
        {helperText && !error && (
          <p
            id={`${inputId}-helper`}
            className="mt-1 text-sm text-muted-foreground"
          >
            {helperText}
          </p>
        )}
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }
